// Pages/Support/WithdrawRequests.jsx
import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Stack
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  completeWithdrawal
} from "../../store/Slices/withdrawSlice";

const fmt = (n, d = 8) => Number(n ?? 0).toFixed(d);
const dt = (v) => new Date(v).toLocaleString();

const StatusChip = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const map = {
    pending:  { color: "warning", label: "Pending" },
    approved: { color: "info",    label: "Approved" },
    completed:{ color: "success", label: "Completed" },
    rejected: { color: "error",   label: "Rejected" },
  };
  const conf = map[s] || { color: "default", label: status || "—" };
  return <Chip size="small" color={conf.color} label={conf.label} variant="outlined" />;
};

export default function WithdrawRequests() {
  const dispatch = useDispatch();
  const { all = [], loading } = useSelector((s) => s.withdraw ?? {});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [reason, setReason] = useState("");
  const [txid, setTxid] = useState("");

  useEffect(() => {
    dispatch(fetchAllWithdrawals());
  }, [dispatch]);

  const copy = async (v) => { try { await navigator.clipboard.writeText(v || ""); } catch {} };

  const onApprove = (id) => {
    dispatch(approveWithdrawal(id)).then(() => {
      dispatch(fetchAllWithdrawals());
    });
  };

  const onReject = (id) => {
    setCurrentId(id);
    setReason("");
    setRejectOpen(true);
  };

  const onComplete = (id) => {
    setCurrentId(id);
    setTxid("");
    setCompleteOpen(true);
  };

  const confirmReject = () => {
    dispatch(rejectWithdrawal({ id: currentId, reason })).then(() => {
      setRejectOpen(false);
      setReason("");
      dispatch(fetchAllWithdrawals());
    });
  };

  const confirmComplete = () => {
    dispatch(completeWithdrawal({ id: currentId, txid })).then(() => {
      setCompleteOpen(false);
      setTxid("");
      dispatch(fetchAllWithdrawals());
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Withdrawal Requests (Support)</Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={() => dispatch(fetchAllWithdrawals())} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {all.length === 0 ? (
            <Typography color="text.secondary">No requests found.</Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 1.2 }}>
              {all.map((it) => (
                <Box
                  key={it._id}
                  sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">
                        {it.asset_symbol} • {it.network} • <StatusChip status={it.status} />
                      </Typography>
                      <Typography variant="body2">
                        User: <b>{it.user_id?.username || "—"}</b> ({it.user_id?.email || "—"})
                      </Typography>
                      <Typography variant="body2">
                        Amount: {fmt(it.amount)} {it.asset_symbol} • Fee: {fmt(it.fee_asset)} {it.asset_symbol} (≈ {fmt(it.fee_usdt,2)} USDT) • Net: {fmt(it.net_amount)} {it.asset_symbol}
                      </Typography>
                      <Typography variant="body2">
                        To: {it.to_address}{" "}
                        <Tooltip title="Copy address">
                          <IconButton size="small" onClick={() => copy(it.to_address)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created: {dt(it.created_at)} {it.updated_at ? ` • Updated: ${dt(it.updated_at)}` : ""}
                      </Typography>
                      {it.reject_reason && (
                        <Typography variant="caption" color="error">Reason: {it.reject_reason}</Typography>
                      )}
                      {it.txid && (
                        <Typography variant="body2">
                          TXID: {it.txid}{" "}
                          <Tooltip title="Copy TXID">
                            <IconButton size="small" onClick={() => copy(it.txid)}>
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      {it.status === "Pending" && (
                        <>
                          <Button variant="contained" color="success" onClick={() => onApprove(it._id)}>
                            Approve
                          </Button>
                          <Button variant="outlined" color="error" onClick={() => onReject(it._id)}>
                            Reject
                          </Button>
                        </>
                      )}
                      {it.status === "Approved" && (
                        <Button variant="contained" onClick={() => onComplete(it._id)}>
                          Mark as Completed
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmReject}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Complete Withdrawal</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="TXID (optional)"
            value={txid}
            onChange={(e) => setTxid(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmComplete}>Mark Completed</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
